// ContractForm.js
import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Paper,
  Divider,
  useTheme,
} from "@mui/material";
import { Close, Save, Add, Delete } from "@mui/icons-material";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import { useCollection } from "../../hooks/useCollection";
import {
  CONTRACT_STATUS,
  CONTRACT_STATUS_LABELS,
} from "../../utils/models/contractModel";
import { PROJECT_STATUS } from "../../utils/models/projectModel";

const ContractForm = ({ contract, onSave, onCancel }) => {
  const theme = useTheme();
  const { add: addContract, update: updateContract } =
    useAuditFirestore("contracts");
  const { add: addProject } = useAuditFirestore("projects");
  const { data: proposals } = useCollection("proposals");
  const { data: clients } = useCollection("clients");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    title: contract?.title || "",
    description: contract?.description || "",
    proposalId: contract?.proposalId || "",
    proposalTitle: contract?.proposalTitle || "",
    clientId: contract?.clientId || "",
    clientName: contract?.clientName || "",
    clientDni: contract?.clientDni || "",
    clientPhone: contract?.clientPhone || "",
    clientEmail: contract?.clientEmail || "",
    address: {
      street: contract?.address?.street || "",
      city: contract?.address?.city || "",
      province: contract?.address?.province || "",
      zipCode: contract?.address?.zipCode || "",
      country: contract?.address?.country || "España",
    },
    files: contract?.files || [],
    version: contract?.version || "1.0",
    versions: contract?.versions || [],
    status: contract?.status || CONTRACT_STATUS.PENDING,
    notes: contract?.notes || "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nameParts = name.split(".");

    if (nameParts.length === 2) {
      setFormData((prev) => ({
        ...prev,
        [nameParts[0]]: {
          ...prev[nameParts[0]],
          [nameParts[1]]: value,
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (error) setError("");
  };

  // Manejar archivos
  const handleFileChange = (index, field, value) => {
    const newFiles = [...formData.files];
    newFiles[index] = { ...newFiles[index], [field]: value };
    setFormData(prev => ({ ...prev, files: newFiles }));
  };

  const handleAddFile = () => {
    setFormData(prev => ({
      ...prev,
      files: [...prev.files, { name: "", url: "" }]
    }));
  };

  const handleRemoveFile = (index) => {
    const newFiles = formData.files.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, files: newFiles }));
  };

  const handleProposalSelect = (proposalId) => {
    const proposal = proposals?.find((p) => p.id === proposalId);
    if (proposal) {
      const client = clients?.find((c) => c.id === proposal.clientId);

      setFormData((prev) => ({
        ...prev,
        proposalId,
        proposalTitle: proposal.title,
        clientId: proposal.clientId,
        clientName: proposal.clientName,
        clientEmail: proposal.clientEmail,
        clientDni: client?.dni || client?.taxId || prev.clientDni,
        clientPhone: client?.phone || prev.clientPhone,
        clientType: client?.type || "individual",
        description: proposal.description || "",
        address: {
          street: proposal.address?.street || prev.address?.street || "",
          city: proposal.address?.city || prev.address?.city || "",
          province: proposal.address?.province || prev.address?.province || "",
          zipCode: proposal.address?.zipCode || prev.address?.zipCode || "",
          country: proposal.address?.country || "España",
        },
        title: proposal.title,
      }));
    }
  };

  const handleVersionChange = () => {
    if (
      formData.status === CONTRACT_STATUS.MODIFICATION &&
      contract?.status !== CONTRACT_STATUS.MODIFICATION
    ) {
      const currentVersion = parseFloat(formData.version || "1.0");
      const newVersion = (currentVersion + 0.1).toFixed(1);

      const newVersionEntry = {
        version: newVersion,
        date: new Date().toISOString(),
        previousStatus: contract?.status || CONTRACT_STATUS.PENDING,
        notes: `Modificación desde estado: ${CONTRACT_STATUS_LABELS[contract?.status] || "Pendiente"}`,
      };

      setFormData((prev) => ({
        ...prev,
        version: newVersion,
        versions: [...(prev.versions || []), newVersionEntry],
      }));
    }
  };

  useEffect(() => {
    handleVersionChange();
  }, [formData.status]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title) {
      setError("El título es obligatorio");
      return;
    }

    // Filtrar archivos vacíos
    const validFiles = formData.files.filter(file => file.name.trim() !== "" && file.url.trim() !== "");

    setLoading(true);
    setError("");

    try {
      const contractData = {
        ...formData,
        files: validFiles,
        updatedAt: new Date().toISOString(),
      };

      const isJustSigned =
        formData.status === CONTRACT_STATUS.SIGNED &&
        contract?.status !== CONTRACT_STATUS.SIGNED;

      if (contract?.id) {
        await updateContract(contract.id, contractData);

        if (isJustSigned) {
          await addProject({
            contractId: contract.id,
            contractTitle: formData.title,
            contractSignedDate: new Date().toISOString(),
            name: formData.title,
            description: `Proyecto generado desde el contrato: ${formData.title}`,
            clientId: formData.clientId,
            clientName: formData.clientName,
            clientPhone: formData.clientPhone || "",
            address: formData.address,
            status: PROJECT_STATUS.MANAGING,
            budget: 0,
            team: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: "",
            isActive: true,
          });
        }
      } else {
        const newContractId = await addContract({
          ...contractData,
          version: "1.0",
          versions: [],
          createdAt: new Date().toISOString(),
          createdBy: "",
          isActive: true,
        });

        if (formData.status === CONTRACT_STATUS.SIGNED) {
          await addProject({
            contractId: newContractId,
            contractTitle: formData.title,
            contractSignedDate: new Date().toISOString(),
            name: formData.title,
            description: `Proyecto generado desde el contrato: ${formData.title}`,
            clientId: formData.clientId,
            clientName: formData.clientName,
            clientPhone: formData.clientPhone || "",
            address: formData.address,
            status: PROJECT_STATUS.MANAGING,
            budget: 0,
            team: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: "",
            isActive: true,
          });
        }
      }

      onSave();
    } catch (err) {
      console.error("❌ Error:", err);
      setError(err.message || "Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.background.paper,
      height: { xs: 40, sm: 44 },
    },
    "& .MuiInputLabel-root": {
      backgroundColor: theme.palette.background.paper,
      paddingInline: "4px",
      fontSize: { xs: "0.8rem", sm: "0.875rem" },
    },
  };

  const sectionTitleSx = {
    fontWeight: 700,
    fontSize: { xs: 14, sm: 15 },
    mb: { xs: 1.5, sm: 2 },
    mt: { xs: 4, sm: 5 },
    color: theme.palette.primary.main,
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 4 },
        borderRadius: { xs: "16px", sm: "24px" },
        border: "1px solid",
        borderColor:
          theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#e2e8f0",
        background: theme.palette.background.paper,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: { xs: 3, sm: 4 },
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" } }}>
            {contract?.id ? "Editar contrato" : "Nuevo contrato"}
          </Typography>
          {contract?.version && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: "block", fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
            >
              Versión {contract.version}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={onCancel}
          sx={{
            background: theme.palette.background.paper,
            border: "1px solid",
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.12)"
                : "#e2e8f0",
          }}
        >
          <Close sx={{ fontSize: { xs: 20, sm: 24 } }} />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: { xs: 2, sm: 3 }, borderRadius: "12px", fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        {/* PROPUESTA RELACIONADA */}
        <Typography sx={sectionTitleSx}>Propuesta relacionada</Typography>
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>Seleccionar propuesta</InputLabel>
          <Select
            name="proposalId"
            value={formData.proposalId}
            onChange={(e) => handleProposalSelect(e.target.value)}
            label="Seleccionar propuesta"
            sx={{
              borderRadius: "12px",
              background: theme.palette.background.paper,
              height: { xs: 40, sm: 44 },
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
            }}
          >
            {proposals
              ?.filter((p) => p.isActive !== false)
              .map((p) => (
                <MenuItem key={p.id} value={p.id} sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>
                  {p.title}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        {/* DATOS DEL CONTRATO */}
        <Typography sx={sectionTitleSx}>Datos del contrato</Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1.5, sm: 2.5 },
            width: "100%",
          }}
        >
          <TextField
            fullWidth
            size="small"
            label="Título"
            name="title"
            value={formData.title}
            onChange={handleChange}
            sx={fieldSx}
            required
            inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
          />

          <TextField
            fullWidth
            multiline
            rows={5}
            label="Descripción"
            name="description"
            value={formData.description}
            onChange={handleChange}
            inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "16px",
                background: theme.palette.background.paper,
              },
            }}
          />

          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2.5 },
              width: "100%",
              flexWrap: "wrap",
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Nombre del cliente"
              name="clientName"
              value={formData.clientName}
              onChange={handleChange}
              sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "280px" } }}
              inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
            />
            <TextField
              fullWidth
              size="small"
              label="DNI/CIF"
              name="clientDni"
              value={formData.clientDni}
              onChange={handleChange}
              sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "220px" } }}
              inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2.5 },
              width: "100%",
              flexWrap: "wrap",
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Email"
              name="clientEmail"
              value={formData.clientEmail}
              onChange={handleChange}
              sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "280px" } }}
              inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
            />
            <TextField
              fullWidth
              size="small"
              label="Teléfono"
              name="clientPhone"
              value={formData.clientPhone}
              onChange={handleChange}
              sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "220px" } }}
              inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
            />
          </Box>
        </Box>

        {/* DIRECCIÓN */}
        <Typography sx={sectionTitleSx}>Dirección</Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.5, sm: 2.5 }, width: "100%" }}>
          <Box sx={{ display: "flex", gap: { xs: 1.5, sm: 2.5 }, width: "100%", flexWrap: "wrap" }}>
            <TextField fullWidth size="small" label="Calle" name="address.street" value={formData.address.street} onChange={handleChange} sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "300px" } }} inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }} />
            <TextField fullWidth size="small" label="Población/Ciudad" name="address.city" value={formData.address.city} onChange={handleChange} sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "300px" } }} inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }} />
          </Box>
          <Box sx={{ display: "flex", gap: { xs: 1.5, sm: 2.5 }, width: "100%", flexWrap: "wrap" }}>
            <TextField fullWidth size="small" label="Provincia" name="address.province" value={formData.address.province} onChange={handleChange} sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "200px" } }} inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }} />
            <TextField fullWidth size="small" label="Código Postal" name="address.zipCode" value={formData.address.zipCode} onChange={handleChange} sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "200px" } }} inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }} />
            <TextField fullWidth size="small" label="País" name="address.country" value={formData.address.country} onChange={handleChange} sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "200px" } }} inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }} />
          </Box>
        </Box>

        {/* ARCHIVOS */}
        <Typography sx={sectionTitleSx}>Archivos</Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%" }}>
          {formData.files.map((file, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                gap: { xs: 1, sm: 1.5 },
                alignItems: "center",
                width: "100%",
                flexWrap: { xs: "wrap", sm: "nowrap" },
              }}
            >
              <TextField
                size="small"
                label="Nombre del archivo"
                value={file.name}
                onChange={(e) => handleFileChange(index, "name", e.target.value)}
                sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "200px" } }}
                inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
              />
              <TextField
                size="small"
                label="Enlace (URL)"
                value={file.url}
                onChange={(e) => handleFileChange(index, "url", e.target.value)}
                sx={{ ...fieldSx, flex: 2, minWidth: { xs: "100%", sm: "300px" } }}
                inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
              />
              <IconButton
                onClick={() => handleRemoveFile(index)}
                size="small"
                sx={{
                  width: { xs: 40, sm: 44 },
                  height: { xs: 40, sm: 44 },
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: theme.palette.error.main,
                  color: theme.palette.error.main,
                  flexShrink: 0,
                  '&:hover': {
                    backgroundColor: theme.palette.error.main,
                    color: "white",
                  },
                }}
              >
                <Delete sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </IconButton>
            </Box>
          ))}
          
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={handleAddFile}
            sx={{
              borderRadius: "12px",
              height: { xs: 40, sm: 44 },
              textTransform: "none",
              fontSize: { xs: "0.8rem", sm: "0.85rem" },
              alignSelf: "flex-start",
              mt: 1,
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              '&:hover': {
                borderColor: theme.palette.primary.dark,
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(0, 175, 163, 0.1)' 
                  : 'rgba(0, 95, 101, 0.05)',
              },
            }}
          >
            Añadir archivo
          </Button>
        </Box>

        {/* ESTADO */}
        <Typography sx={sectionTitleSx}>Estado</Typography>
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>Estado</InputLabel>
          <Select
            name="status"
            value={formData.status}
            onChange={handleChange}
            label="Estado"
            sx={{ borderRadius: "12px", background: theme.palette.background.paper, height: { xs: 40, sm: 44 }, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
          >
            <MenuItem value="pending" sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>Pendiente</MenuItem>
            <MenuItem value="created" sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>Creado</MenuItem>
            <MenuItem value="sent" sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>Enviado</MenuItem>
            <MenuItem value="modification" sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>Modificación</MenuItem>
            <MenuItem value="rejected" sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>Rechazado</MenuItem>
            <MenuItem value="signed" sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>Firmado</MenuItem>
          </Select>
        </FormControl>

        {/* VERSIÓN ACTUAL */}
        {formData.version && (
          <>
            <Typography sx={sectionTitleSx}>Versión</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" fontWeight={600} color="primary" sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}>
                Versión actual: {formData.version}
              </Typography>
              {formData.versions?.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                  ({formData.versions.length} cambios registrados)
                </Typography>
              )}
            </Box>
          </>
        )}

        {/* NOTAS */}
        <Typography sx={sectionTitleSx}>Notas internas</Typography>
        <TextField
          fullWidth multiline rows={4} label="Notas" name="notes"
          value={formData.notes} onChange={handleChange}
          inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "16px", background: theme.palette.background.paper } }}
        />

        <Divider sx={{ mt: { xs: 4, sm: 5 }, mb: { xs: 2, sm: 3 } }} />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, flexWrap: "wrap" }}>
          <Button variant="outlined" onClick={onCancel}
            sx={{ borderRadius: "12px", px: { xs: 2, sm: 3 }, height: { xs: 40, sm: 44 }, textTransform: "none", fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
            fullWidth={{ xs: true, sm: false }}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={loading} startIcon={<Save />}
            sx={{ borderRadius: "12px", px: { xs: 2, sm: 3 }, height: { xs: 40, sm: 44 }, boxShadow: "none", textTransform: "none", fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
            fullWidth={{ xs: true, sm: false }}>
            {loading ? "Guardando..." : contract?.id ? "Actualizar" : "Crear contrato"}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default ContractForm;